import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Stack, Card, CardContent,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, FormControl, InputLabel, Select, MenuItem, Grid, Divider,
  Alert
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BookIcon from '@mui/icons-material/Book';
import RoomIcon from '@mui/icons-material/Room';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { 
  bulkCreateSchedules, 
  fetchSchedules, 
  fetchClassSchedule, 
  deleteSchedule,
  fetchDraftSchedules,
  bulkCreateDraftSchedules,
  deleteDraftSchedule,
  selectSchedules
} from '../../slices/scheduleSlice';
import { fetchClassSubjects } from '../../slices/subjectsSlice';
import { fetchTeacherAbsences } from '../../slices/staffSlice';
import { useToast } from '../../context/ToastContext';
import { getPeriodLabel } from '../../utils/periodLabels';
import { isTeacher, TEACHER_PERMISSIONS } from '../../config/teacherPermissions';

const EducationalScheduleClassView = ({
  schedules,
  periodTimings,
  selectedClass,
  selectedTeacher,
  dateFilter,
  selectedDate,
  classList,
  staffList = []
}) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [editMode, setEditMode] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDayForEdit, setSelectedDayForEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    subject: '',
    teacher: '',
    classroom: '',
    notes: ''
  });

  const { classSubjects = [], subjects: allSubjects = [] } = useSelector((state) => state.subjects);
  const allSchedules = useSelector(selectSchedules);
  const loading = useSelector((state) => state.schedule.loading);
  const draftSchedules = useSelector((state) => state.schedule.draftSchedules || []);
  
  // Get teacher absences for the selected date
  const { teacherAbsences } = useSelector((state) => state.staff);
  
  // Fetch absences when date changes (for daily view) or when filter changes
  useEffect(() => {
    // For daily views, fetch absences for the specific date
    if (selectedDate && (dateFilter === 'today' || dateFilter === 'daily' || dateFilter === 'yesterday' || dateFilter === 'tomorrow')) {
      const dateStr = selectedDate.toISOString().split('T')[0];
      dispatch(fetchTeacherAbsences({ absence_date: dateStr }));
    }
    // For weekly/monthly views, we might want to fetch all absences in the range
    // For now, we'll rely on the absences already fetched or fetch without date filter
    else if (dateFilter === 'weekly' || dateFilter === 'monthly') {
      // Fetch all absences (no date filter) to show all absent teachers
      dispatch(fetchTeacherAbsences({}));
    }
  }, [selectedDate, dateFilter, dispatch]);
  
  // Get absent teacher IDs for the selected date or date range
  const absentTeacherIds = useMemo(() => {
    // For daily views, filter by specific date
    if (selectedDate && (dateFilter === 'today' || dateFilter === 'daily' || dateFilter === 'yesterday' || dateFilter === 'tomorrow')) {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const absentIds = teacherAbsences
        .filter(a => {
          const absenceDate = a.absence_date;
          const dateMatch = absenceDate === dateStr || (typeof absenceDate === 'string' && absenceDate.startsWith(dateStr));
          return dateMatch;
        })
        .map(a => String(a.teacher)); // Convert to string for consistent comparison
      
      console.log('🔴 Absent teacher IDs for', dateStr, ':', absentIds);
      return absentIds;
    }
    // For weekly/monthly views, include all absent teachers (all dates)
    else if (dateFilter === 'weekly' || dateFilter === 'monthly') {
      const absentIds = teacherAbsences.map(a => String(a.teacher));
      console.log('🔴 All absent teacher IDs (weekly/monthly):', absentIds);
      return absentIds;
    }
    
    console.log('⚠️ No date filter or selectedDate for absentTeacherIds');
    return [];
  }, [teacherAbsences, selectedDate, dateFilter]);
  
  // Get subjects for the editing class
  const subjectsList = useMemo(() => {
    if (editingCell?.classId && Array.isArray(classSubjects)) {
      return classSubjects.filter(s => s.school_class === editingCell.classId && !s.is_archived);
    }
    if (Array.isArray(allSubjects)) {
      return allSubjects.filter(s => !s.is_archived);
    }
    return [];
  }, [classSubjects, allSubjects, editingCell]);
  // Get day name in Arabic
  const getDayName = (dayValue) => {
    const days = {
      'sunday': 'الأحد',
      'monday': 'الاثنين',
      'tuesday': 'الثلاثاء',
      'wednesday': 'الأربعاء',
      'thursday': 'الخميس',
      'friday': 'الجمعة',
      'saturday': 'السبت'
    };
    return days[dayValue] || dayValue;
  };

  // Get current day based on date filter
  const getCurrentDay = () => {
    const today = new Date();
    const dayOfWeekMap = {
      0: 'sunday',
      1: 'monday',
      2: 'tuesday',
      3: 'wednesday',
      4: 'thursday',
      5: 'friday',
      6: 'saturday'
    };

    switch (dateFilter) {
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return dayOfWeekMap[yesterday.getDay()];
      case 'today':
      case 'daily':
        return dayOfWeekMap[today.getDay()];
      case 'tomorrow':
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return dayOfWeekMap[tomorrow.getDay()];
      default:
        return null; // For weekly/monthly, show all days
    }
  };

  // Get time slots
  const timeSlots = useMemo(() => {
    if (periodTimings && periodTimings.length > 0) {
      return periodTimings
        .filter(t => t.is_active)
        .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
    }
    // Default time slots
    return [
      { id: 'period_1', start_time: '08:00', end_time: '08:45' },
      { id: 'period_2', start_time: '08:45', end_time: '09:30' },
      { id: 'period_3', start_time: '09:30', end_time: '10:15' },
      { id: 'period_4', start_time: '10:30', end_time: '11:15' },
      { id: 'period_5', start_time: '11:15', end_time: '12:00' }
    ];
  }, [periodTimings]);

  // Get classes to display
  const classesToShow = useMemo(() => {
    if (selectedClass !== 'all') {
      const cls = classList.find(c => c.id === selectedClass);
      return cls ? [cls] : [];
    }
    // Get unique classes from schedules
    const classMap = new Map();
    schedules.forEach(schedule => {
      if (schedule.school_class && schedule.school_class_name) {
        if (!classMap.has(schedule.school_class)) {
          classMap.set(schedule.school_class, {
            id: schedule.school_class,
            name: schedule.school_class_name
          });
        }
      }
    });
    return Array.from(classMap.values()).sort((a, b) => a.name.localeCompare(b.name, 'ar'));
  }, [schedules, selectedClass, classList]);

  // Note: schedules prop is already filtered by parent component
  // Merge with draft schedules (drafts take precedence)
  const filteredSchedules = useMemo(() => {
    const baseSchedules = schedules || [];
    const drafts = draftSchedules || [];
    
    console.log('🔄 Merging schedules:', {
      baseCount: baseSchedules.length,
      draftsCount: drafts.length,
      drafts: drafts,
      selectedClass: selectedClass
    });
    
    // Filter drafts by selected class if needed
    let filteredDrafts = drafts;
    if (selectedClass !== 'all') {
      filteredDrafts = drafts.filter(draft => {
        const draftClassId = draft.school_class || draft.school_class_id;
        return draftClassId === selectedClass;
      });
    }
    
    console.log('📋 Filtered drafts:', {
      before: drafts.length,
      after: filteredDrafts.length,
      selectedClass
    });
    
    // Create a map of drafts by key (class_day_slot)
    const draftMap = new Map();
    filteredDrafts.forEach(draft => {
      // Handle both UUID and string formats for school_class
      const classId = draft.school_class || draft.school_class_id;
      const key = `${classId}_${draft.day_of_week}_${draft.slot_id}`;
      draftMap.set(key, { ...draft, school_class: classId });
    });
    
    // Start with base schedules, but replace with drafts where they exist
    const mergedSchedules = [];
    const processedKeys = new Set();
    
    // First, add all drafts (they take precedence)
    filteredDrafts.forEach(draft => {
      const classId = draft.school_class || draft.school_class_id;
      const key = `${classId}_${draft.day_of_week}_${draft.slot_id}`;
      mergedSchedules.push({
        ...draft,
        school_class: classId, // Ensure consistent format
        is_draft: true
      });
      processedKeys.add(key);
    });
    
    // Then add base schedules (original) that don't have drafts
    // Mark them as original (not editable from this view)
    baseSchedules.forEach(schedule => {
      const classId = schedule.school_class || schedule.school_class_id;
      const key = `${classId}_${schedule.day_of_week}_${schedule.slot_id}`;
      if (!processedKeys.has(key)) {
        mergedSchedules.push({
          ...schedule,
          is_original: true,
          is_draft: false
        });
      }
    });
    
    console.log('✅ Merged schedules:', {
      total: mergedSchedules.length,
      drafts: mergedSchedules.filter(s => s.is_draft).length,
      originals: mergedSchedules.filter(s => s.is_original).length,
      sampleDraft: mergedSchedules.find(s => s.is_draft),
      sampleOriginal: mergedSchedules.find(s => s.is_original)
    });
    
    return mergedSchedules;
  }, [schedules, draftSchedules, selectedClass]);

  // Build schedule grid: classes as columns, time slots as rows
  const scheduleGrid = useMemo(() => {
    const grid = {};

    // Initialize grid with time slots - prioritize period as the key
    timeSlots.forEach(slot => {
      const periodValue = slot.period;
      const slotId = slot.id;
      const timeKey = slot.start_time ? (typeof slot.start_time === 'string' ? slot.start_time.substring(0, 5) : String(slot.start_time).substring(0, 5)) : null;
      
      // Always use period as primary key if available, otherwise fallback
      const primaryKey = periodValue || slotId || timeKey;
      if (!primaryKey) {
        console.warn('⚠️ Time slot missing all keys:', slot);
        return; // Skip if no key available
      }
      
      grid[primaryKey] = {
        period: periodValue,
        id: slotId,
        start_time: timeKey,
        slot: slot,
        classes: {},
        // Store alternative keys for matching
        altKeys: [periodValue, slotId, timeKey].filter(Boolean)
      };
      
      classesToShow.forEach(cls => {
        grid[primaryKey].classes[cls.id] = [];
      });
    });
    
    console.log('📊 Grid initialized with keys:', Object.keys(grid));
    console.log('📊 Grid structure:', Object.keys(grid).map(key => ({
      key,
      period: grid[key].period,
      start_time: grid[key].start_time,
      classes: Object.keys(grid[key].classes)
    })));

    // Populate grid - match schedules to time slots
    // Track which schedules we've already added to prevent duplicates
    const addedScheduleIds = new Set();
    
    // Debug: Log grid keys and schedules
    console.log('🔍 Grid keys:', Object.keys(grid));
    console.log('🔍 Filtered schedules count:', filteredSchedules.length);
    console.log('🔍 Sample schedule:', filteredSchedules[0]);
    
    filteredSchedules.forEach(schedule => {
      const classId = schedule.school_class;
      if (!classId) {
        console.warn('⚠️ Schedule missing school_class:', schedule);
        return;
      }
      
      // Skip if we've already added this schedule
      if (addedScheduleIds.has(schedule.id)) return;
      
      let matchedKey = null;
      
      // Strategy 1: Direct match by slot_id (most reliable)
      // schedule.slot_id should match period value exactly (e.g., "period_1")
      const slotId = schedule.slot_id;
      if (slotId) {
        // Try exact match first with period
        matchedKey = Object.keys(grid).find(key => {
          const slotInfo = grid[key];
          return slotInfo.period === slotId;
        });
        
        // If no match, try with altKeys
        if (!matchedKey) {
          matchedKey = Object.keys(grid).find(key => {
            const slotInfo = grid[key];
            return slotInfo.altKeys?.includes(slotId);
          });
        }
      }
      
      // Strategy 2: Match by start_time (fallback)
      if (!matchedKey && schedule.start_time) {
        const scheduleTime = typeof schedule.start_time === 'string' 
          ? schedule.start_time.substring(0, 5)
          : String(schedule.start_time).substring(0, 5);
        
        // Find slot with matching start_time
        matchedKey = Object.keys(grid).find(key => {
          const slotInfo = grid[key];
          if (!slotInfo.start_time) return false;
          const slotTime = typeof slotInfo.start_time === 'string'
            ? slotInfo.start_time.substring(0, 5)
            : String(slotInfo.start_time).substring(0, 5);
          return slotTime === scheduleTime;
        });
      }
      
      // Add schedule to grid if matched
      if (matchedKey && grid[matchedKey] && grid[matchedKey].classes && grid[matchedKey].classes[classId]) {
        grid[matchedKey].classes[classId].push(schedule);
        addedScheduleIds.add(schedule.id);
        console.log('✅ Matched schedule:', {
          slot_id: slotId,
          matched_key: matchedKey,
          subject: schedule.subject_name,
          class: schedule.school_class_name
        });
      } else {
        console.warn('❌ Could not match schedule:', {
          slot_id: slotId,
          start_time: schedule.start_time,
          subject: schedule.subject_name,
          class: schedule.school_class_name,
          available_keys: Object.keys(grid)
        });
      }
    });

    return grid;
  }, [filteredSchedules, timeSlots, classesToShow]);

  // Check if we have any schedules in the grid (must be before conditional returns)
  const hasSchedulesInGrid = useMemo(() => {
    return Object.keys(scheduleGrid).some(key => {
      const slotInfo = scheduleGrid[key];
      return Object.keys(slotInfo.classes || {}).some(classId => 
        slotInfo.classes[classId].length > 0
      );
    });
  }, [scheduleGrid]);

  // Calculate statistics from filteredSchedules (includes temporary changes)
  const statistics = useMemo(() => {
    const teacherStats = {};
    const subjectStats = {};
    
    filteredSchedules.forEach(schedule => {
      // Teacher statistics
      if (schedule.teacher || schedule.teacher_id) {
        const teacherId = schedule.teacher || schedule.teacher_id;
        const teacherName = schedule.teacher_name || 
          (staffList.find(t => t.id === teacherId) 
            ? `${staffList.find(t => t.id === teacherId).first_name} ${staffList.find(t => t.id === teacherId).last_name}`
            : 'غير محدد');
        
        if (!teacherStats[teacherId]) {
          teacherStats[teacherId] = {
            name: teacherName,
            totalHours: 0,
            totalMinutes: 0,
            classCount: 0,
            subjectCount: new Set()
          };
        }
        
        const minutes = schedule.duration_minutes || 45;
        teacherStats[teacherId].totalMinutes += minutes;
        teacherStats[teacherId].totalHours = teacherStats[teacherId].totalMinutes / 60;
        teacherStats[teacherId].classCount++;
        if (schedule.subject_name) {
          teacherStats[teacherId].subjectCount.add(schedule.subject_name);
        }
      }
      
      // Subject statistics
      if (schedule.subject || schedule.subject_id) {
        const subjectId = schedule.subject || schedule.subject_id;
        const subjectName = schedule.subject_name || 'غير محدد';
        
        if (!subjectStats[subjectId]) {
          subjectStats[subjectId] = {
            name: subjectName,
            totalHours: 0,
            totalMinutes: 0,
            classCount: 0,
            teacherCount: new Set()
          };
        }
        
        const minutes = schedule.duration_minutes || 45;
        subjectStats[subjectId].totalMinutes += minutes;
        subjectStats[subjectId].totalHours = subjectStats[subjectId].totalMinutes / 60;
        subjectStats[subjectId].classCount++;
        if (schedule.teacher_name || schedule.teacher) {
          subjectStats[subjectId].teacherCount.add(schedule.teacher_name || schedule.teacher);
        }
      }
    });
    
    const teacherStatsArray = Object.values(teacherStats).map(stat => ({
      ...stat,
      subjectCount: stat.subjectCount.size
    }));
    const subjectStatsArray = Object.values(subjectStats).map(stat => ({
      ...stat,
      teacherCount: stat.teacherCount.size
    }));
    
    return { teacherStatsArray, subjectStatsArray };
  }, [filteredSchedules, staffList]);

  // Load subjects for the selected class when opening edit dialog (must be before conditional returns)
  React.useEffect(() => {
    if (editDialogOpen && editingCell) {
      dispatch(fetchClassSubjects(editingCell.classId));
    }
  }, [editDialogOpen, editingCell, dispatch]);

  // Update form data when subjects are loaded (fixes Select error)
  useEffect(() => {
    if (editDialogOpen && editingCell && editingCell.schedules && editingCell.schedules.length > 0 && subjectsList.length > 0) {
      const existingSchedule = editingCell.schedules[0];
      const subjectId = existingSchedule.subject?.id || existingSchedule.subject || existingSchedule.subject_id || '';
      const teacherId = existingSchedule.teacher?.id || existingSchedule.teacher || existingSchedule.teacher_id || '';
      
      // Only update if the current value is empty or invalid
      if (editFormData.subject === '' || !subjectsList.some(s => s.id === editFormData.subject)) {
        const validSubjectId = subjectsList.some(s => s.id === subjectId) ? subjectId : '';
        const validTeacherId = staffList.filter(s => s.is_teacher && !s.is_archived).some(t => t.id === teacherId) ? teacherId : '';
        
        setEditFormData(prev => ({
          ...prev,
          subject: validSubjectId,
          teacher: validTeacherId
        }));
      }
    }
  }, [editDialogOpen, editingCell, subjectsList, staffList, editFormData.subject]);

  // Get current day for filtering drafts
  const getCurrentDayForFetch = () => {
    const today = new Date();
    const dayOfWeekMap = {
      0: 'sunday', 1: 'monday', 2: 'tuesday', 3: 'wednesday',
      4: 'thursday', 5: 'friday', 6: 'saturday'
    };
    
    switch (dateFilter) {
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return dayOfWeekMap[yesterday.getDay()];
      case 'today':
      case 'daily':
        return dayOfWeekMap[today.getDay()];
      case 'tomorrow':
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return dayOfWeekMap[tomorrow.getDay()];
      case 'weekly':
      case 'monthly':
        return null; // Fetch all days for weekly/monthly
      default:
        return null;
    }
  };

  // Fetch draft schedules on component mount and when filters change
  useEffect(() => {
    const params = {};
    if (selectedClass !== 'all') {
      params.school_class = selectedClass;
    }
    const currentDay = getCurrentDayForFetch();
    if (currentDay) {
      params.day_of_week = currentDay;
    }
    console.log('📥 Fetching draft schedules with params:', params);
    dispatch(fetchDraftSchedules(params));
  }, [dispatch, selectedClass, dateFilter]);

  // Render schedule cell
  const renderScheduleCell = (slotId, classId) => {
    const slotSchedules = scheduleGrid[slotId]?.classes?.[classId] || [];
    
    const cellSx = {
      minHeight: 80,
      p: 1,
      cursor: editMode ? 'pointer' : 'default',
      position: 'relative',
      '&:hover': editMode ? {
        bgcolor: 'action.hover',
        '&::after': {
          content: slotSchedules.length === 0 ? '"+"' : '"✏️"',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '1.5rem',
          opacity: 0.5
        }
      } : {}
    };
    
    if (slotSchedules.length === 0) {
      return (
        <TableCell 
          sx={cellSx}
          onClick={() => handleCellClick(slotId, classId)}
        >
          {editMode ? (
            <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
              <AddIcon sx={{ fontSize: 24, opacity: 0.3 }} />
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                إضافة
              </Typography>
            </Box>
          ) : (
            '-'
          )}
        </TableCell>
      );
    }

    return (
      <TableCell 
        sx={cellSx}
        onClick={() => handleCellClick(slotId, classId)}
      >
        <Stack spacing={0.5}>
          {slotSchedules.map((schedule, idx) => {
            // Check if teacher is absent
            const teacherId = schedule.teacher || schedule.teacher_id;
            // Convert to string for consistent comparison
            const teacherIdStr = teacherId ? String(teacherId) : null;
            const isAbsent = teacherIdStr && absentTeacherIds.includes(teacherIdStr);
            
            // Debug log for absent teachers
            if (isAbsent) {
              console.log('🔴 Found absent teacher schedule:', {
                teacherId: teacherIdStr,
                subject: schedule.subject_name,
                class: schedule.school_class_name,
                absentIds: absentTeacherIds
              });
            }
            
            return (
              <Card
                key={idx}
                elevation={1}
                sx={{
                  p: 1,
                  bgcolor: isAbsent 
                    ? 'error.light' 
                    : (schedule.color ? `${schedule.color}15` : 'primary.light'),
                  border: `2px solid ${isAbsent ? 'error.main' : (schedule.color || 'primary.main')}${isAbsent ? '' : '30'}`,
                  borderRadius: 1,
                  position: 'relative',
                  '&:hover': editMode ? {
                    border: `2px solid ${isAbsent ? 'error.main' : (schedule.color || 'primary.main')}`,
                    '&::after': {
                      content: '"✏️"',
                      position: 'absolute',
                      top: 4,
                      left: 4,
                      fontSize: '0.8rem'
                    }
                  } : {}
                }}
              >
              {isAbsent && (
                <Chip
                  label="غائب"
                  size="small"
                  color="error"
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    fontSize: '0.6rem',
                    height: 18,
                    zIndex: 1
                  }}
                />
              )}
              {schedule.is_draft && (
                <Chip 
                  label="مسودة" 
                  size="small" 
                  color="warning" 
                  sx={{ 
                    position: 'absolute', 
                    top: 2, 
                    left: 2, 
                    fontSize: '0.6rem',
                    height: 18,
                    zIndex: 1
                  }} 
                />
              )}
              {schedule.is_original && !schedule.is_draft && (
                <Chip 
                  label="أصلي" 
                  size="small" 
                  color="info" 
                  sx={{ 
                    position: 'absolute', 
                    top: 2, 
                    left: 2, 
                    fontSize: '0.6rem',
                    height: 18,
                    zIndex: 1,
                    opacity: 0.7
                  }} 
                />
              )}
              <Stack spacing={0.5}>
                <Typography 
                  variant="caption" 
                  fontWeight="bold"
                  sx={{ color: isAbsent ? 'error.dark' : 'inherit' }}
                >
                  {schedule.subject_name || 'غير محدد'}
                </Typography>
                {(schedule.teacher_name || schedule.teacher) && (
                  <Stack useFlexGap direction="row" spacing={0.5} alignItems="center">
                    <PersonIcon sx={{ fontSize: 12 }} />
                    <Typography 
                      variant="caption" 
                      fontSize="0.7rem"
                      sx={{ color: isAbsent ? 'error.dark' : 'inherit', fontWeight: isAbsent ? 'bold' : 'normal' }}
                    >
                      {schedule.teacher_name || (schedule.teacher ? `معلم (${schedule.teacher})` : 'غير محدد')}
                    </Typography>
                  </Stack>
                )}
                {schedule.classroom && (
                  <Stack useFlexGap direction="row" spacing={0.5} alignItems="center">
                    <RoomIcon sx={{ fontSize: 12 }} />
                    <Typography variant="caption" fontSize="0.7rem">
                      {schedule.classroom}
                    </Typography>
                  </Stack>
                )}
                {dateFilter === 'weekly' || dateFilter === 'monthly' ? (
                  <Typography variant="caption" fontSize="0.65rem" color="text.secondary">
                    {getDayName(schedule.day_of_week)}
                  </Typography>
                ) : null}
              </Stack>
            </Card>
            );
          })}
        </Stack>
      </TableCell>
    );
  };

  const getDateDisplay = () => {
    if (!selectedDate) return '';
    
    // Format as DD/MM/YYYY
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const year = selectedDate.getFullYear();
    const dateStr = `${day}/${month}/${year}`;

    switch (dateFilter) {
      case 'yesterday':
        return `أمس (${dateStr})`;
      case 'today':
      case 'daily':
        return `اليوم (${dateStr})`;
      case 'tomorrow':
        return `غداً (${dateStr})`;
      case 'weekly':
        return `الأسبوع الحالي (${dateStr})`;
      case 'monthly':
        return `الشهر الحالي (${dateStr})`;
      default:
        return dateStr;
    }
  };

  if (classesToShow.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          لا توجد صفوف متاحة
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          يرجى اختيار فلاتر مختلفة
        </Typography>
      </Paper>
    );
  }

  if (filteredSchedules.length === 0 && !hasSchedulesInGrid) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          لا توجد جداول متاحة
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          يرجى اختيار فلاتر مختلفة أو إضافة جداول جديدة
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
          عدد الجداول المفلترة: {filteredSchedules.length} | عدد الفترات الزمنية: {timeSlots.length}
        </Typography>
      </Paper>
    );
  }

  // Get current day for editing
  const getCurrentDayForEdit = () => {
    if (selectedDayForEdit) return selectedDayForEdit;
    
    const today = new Date();
    const dayOfWeekMap = {
      0: 'sunday',
      1: 'monday',
      2: 'tuesday',
      3: 'wednesday',
      4: 'thursday',
      5: 'friday',
      6: 'saturday'
    };

    switch (dateFilter) {
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return dayOfWeekMap[yesterday.getDay()];
      case 'today':
      case 'daily':
        return dayOfWeekMap[today.getDay()];
      case 'tomorrow':
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return dayOfWeekMap[tomorrow.getDay()];
      default:
        return null; // For weekly/monthly, we need to select day
    }
  };

  // Handle cell click for editing
  const handleCellClick = (slotId, classId) => {
    if (!editMode) return;
    
    const slotSchedules = scheduleGrid[slotId]?.classes?.[classId] || [];
    const slot = timeSlots.find(s => (s.period || s.id) === slotId);
    const selectedClassObj = classList.find(c => c.id === classId);
    const currentDay = getCurrentDayForEdit();
    
    // Check if there's an original schedule (not editable from here)
    const hasOriginalSchedule = slotSchedules.some(s => s.is_original && !s.is_draft);
    if (hasOriginalSchedule && slotSchedules.length > 0) {
      // If there's an original schedule, we can only create a draft override
      // Don't show a warning, just allow creating a draft
    }
    
    // For weekly/monthly, use the day from existing schedule or require selection
    let dayToUse = currentDay;
    if (!dayToUse && slotSchedules.length > 0) {
      dayToUse = slotSchedules[0].day_of_week;
    }
    
    setEditingCell({
      slotId,
      classId,
      className: selectedClassObj?.name || '',
      slot: slot,
      schedules: slotSchedules,
      dayOfWeek: dayToUse,
      hasOriginal: hasOriginalSchedule
    });
    
    // Reset day selection
    setSelectedDayForEdit(null);
    
    // If there's an existing schedule (draft or original), pre-fill the form
    // But wait for subjects to load before setting the form to avoid Select errors
    if (slotSchedules.length > 0) {
      const existingSchedule = slotSchedules[0];
      // Get subject and teacher IDs - handle both object and ID formats
      const subjectId = existingSchedule.subject?.id || existingSchedule.subject || existingSchedule.subject_id || '';
      const teacherId = existingSchedule.teacher?.id || existingSchedule.teacher || existingSchedule.teacher_id || '';
      
      // Only set form data if subjects are loaded, otherwise set empty and let useEffect handle it
      if (subjectsList.length > 0) {
        // Verify the IDs exist in the available options
        const validSubjectId = subjectsList.some(s => s.id === subjectId) ? subjectId : '';
        const validTeacherId = staffList.filter(s => s.is_teacher && !s.is_archived).some(t => t.id === teacherId) ? teacherId : '';
        
        setEditFormData({
          subject: validSubjectId,
          teacher: validTeacherId,
          classroom: existingSchedule.classroom || '',
          notes: existingSchedule.notes || ''
        });
      } else {
        // Subjects not loaded yet, set empty and wait for them to load
        setEditFormData({
          subject: '',
          teacher: '',
          classroom: existingSchedule.classroom || '',
          notes: existingSchedule.notes || ''
        });
      }
      if (!currentDay) {
        setSelectedDayForEdit(existingSchedule.day_of_week);
      }
    } else {
      setEditFormData({
        subject: '',
        teacher: '',
        classroom: '',
        notes: ''
      });
    }
    
    setEditDialogOpen(true);
  };

  // Auto-fill teacher, classroom and notes when a subject is selected
  const handleSubjectChange = (subjectId) => {
    const subject = subjectsList.find(s => s.id === subjectId);
    const getId = (v) => (v && typeof v === 'object' ? v.id : v);
    const classId = editingCell?.classId;
    // Reuse details from another period of the same subject in this class (drafts first)
    const sameSubjectSchedule = [...draftSchedules, ...allSchedules].find(s =>
      String(getId(s.subject) || s.subject_id) === String(subjectId) &&
      String(getId(s.school_class) || s.school_class_id) === String(classId)
    );
    const teacherId = getId(subject?.teacher) || getId(sameSubjectSchedule?.teacher) || sameSubjectSchedule?.teacher_id;
    const isValidTeacher = staffList.some(t => t.is_teacher && !t.is_archived && t.id === teacherId);

    setEditFormData(prev => ({
      ...prev,
      subject: subjectId,
      teacher: isValidTeacher ? teacherId : prev.teacher,
      classroom: sameSubjectSchedule?.classroom || prev.classroom,
      notes: sameSubjectSchedule?.notes || prev.notes
    }));
  };

  // Handle save schedule - save to draft schedules
  const handleSaveSchedule = async () => {
    if (!editingCell) return;
    
    const { slotId, classId, slot } = editingCell;
    const dayOfWeek = selectedDayForEdit || editingCell.dayOfWeek;
    
    if (!dayOfWeek) {
      showToast('يرجى اختيار يوم محدد', 'warning');
      return;
    }
    
    if (!editFormData.subject) {
      showToast('يرجى اختيار مادة', 'warning');
      return;
    }
    
    // Find the subject and teacher for display
    const subject = subjectsList.find(s => s.id === editFormData.subject);
    const teacher = staffList.find(t => t.id === editFormData.teacher);
    
    if (!subject) {
      showToast('المادة المختارة غير موجودة', 'error');
      return;
    }
    
    // Build draft schedule data
    const scheduleData = {
      day_of_week: dayOfWeek,
      slot_id: slotId,
      slot_name: getPeriodLabel(slot) || slotId,
      slot_type: 'class',
      start_time: slot?.start_time || '08:00',
      end_time: slot?.end_time || '08:45',
      school_class_id: classId,
      subject_id: editFormData.subject,
      teacher_id: editFormData.teacher || null,
      classroom: editFormData.classroom || '',
      notes: editFormData.notes || ''
    };
    
    try {
      // Save to draft schedules
      // The serializer expects 'schedules' field (not 'drafts')
      const result = await dispatch(bulkCreateDraftSchedules({
        schedules: [scheduleData],
        school_class_id: classId
      }));
      console.log('💾 Save draft result:', result);
      
      // The endpoint skips rows it fails to save and still returns 2xx with an errors list
      const saveErrors = result.payload?.errors || [];
      if (result.type.endsWith('/fulfilled') && saveErrors.length > 0) {
        console.error('Draft save errors:', saveErrors);
        showToast('حدث خطأ أثناء حفظ المسودة', 'error');
      } else if (result.type.endsWith('/fulfilled')) {
        showToast('تم حفظ التغيير في المسودات فقط - لم يتم تغيير الجدول الأصلي', 'success');
        setEditDialogOpen(false);
        setEditingCell(null);
        setSelectedDayForEdit(null);
        // Refresh draft schedules ONLY - do NOT refresh original schedules
        dispatch(fetchDraftSchedules({ school_class: classId }));
        // Explicitly do NOT call fetchSchedules() here to ensure original schedules are not affected
      } else {
        showToast('حدث خطأ أثناء حفظ المسودة', 'error');
      }
    } catch (error) {
      console.error('Error saving draft schedule:', error);
      showToast('حدث خطأ أثناء حفظ المسودة', 'error');
    }
  };

  // Handle delete schedule - delete from draft schedules only
  const handleDeleteSchedule = async () => {
    if (!editingCell || editingCell.schedules.length === 0) return;
    
    const scheduleToDelete = editingCell.schedules[0];
    
    // Only allow deleting drafts, not original schedules
    if (!scheduleToDelete.is_draft) {
      showToast('لا يمكن حذف الجدول الأصلي من هنا. يمكنك فقط حذف المسودات', 'warning');
      return;
    }
    
    // If it's a draft, delete it
    if (scheduleToDelete.id) {
      try {
        const result = await dispatch(deleteDraftSchedule(scheduleToDelete.id));
        if (result.type.endsWith('/fulfilled')) {
          showToast('تم حذف المسودة', 'success');
          setEditDialogOpen(false);
          setEditingCell(null);
          setSelectedDayForEdit(null);
          // Refresh draft schedules
          if (editingCell.classId) {
            dispatch(fetchDraftSchedules({ school_class: editingCell.classId }));
          }
        } else {
          showToast('حدث خطأ أثناء حذف المسودة', 'error');
        }
      } catch (error) {
        console.error('Error deleting draft schedule:', error);
        showToast('حدث خطأ أثناء حذف المسودة', 'error');
      }
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack>
          <Typography variant="h6" fontWeight="bold">
            الجدول حسب الصفوف - {getDateDisplay()}
          </Typography>
          {draftSchedules.length > 0 && (
            <Typography variant="caption" color="info.main" sx={{ mt: 0.5 }}>
              ℹ️ لديك {draftSchedules.length} مسودة محفوظة - المسودات لا تغير الجدول الأصلي أبداً وتبقى منفصلة
            </Typography>
          )}
        </Stack>
        <Stack useFlexGap direction="row" spacing={1}>
          <Tooltip title="تحديث المسودات">
            <IconButton 
              size="small" 
              onClick={() => {
                const params = {};
                if (selectedClass !== 'all') {
                  params.school_class = selectedClass;
                }
                const currentDay = getCurrentDayForFetch();
                if (currentDay) {
                  params.day_of_week = currentDay;
                }
                console.log('🔄 Manually refreshing draft schedules:', params);
                dispatch(fetchDraftSchedules(params));
              }}
              disabled={loading.draftSchedules}
            >
              <AccessTimeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!editMode ? (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setEditMode(true)}
            >
              تفعيل التعديل
            </Button>
          ) : (
            <>
              {draftSchedules.length > 0 && (
                <Tooltip title="المسودات محفوظة ولا تغير الجدول الأصلي أبداً">
                  <Chip
                    label={`${draftSchedules.length} مسودة محفوظة`}
                    color="info"
                    icon={<SaveIcon />}
                    sx={{ height: 'auto', py: 1 }}
                  />
                </Tooltip>
              )}
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => {
                  setEditMode(false);
                  setEditingCell(null);
                  setEditDialogOpen(false);
                }}
              >
                إلغاء التعديل
              </Button>
            </>
          )}
        </Stack>
      </Stack>
      
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 150, position: 'sticky', left: 0, zIndex: 10, bgcolor: 'primary.main' }}>
                الوقت
              </TableCell>
              {classesToShow.map(cls => (
                <TableCell
                  key={cls.id}
                  sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', minWidth: 200 }}
                >
                  <Stack useFlexGap direction="row" spacing={1} alignItems="center" justifyContent="center">
                    <SchoolIcon sx={{ fontSize: 18 }} />
                    <Typography>{cls.name}</Typography>
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {timeSlots.map((slot) => {
              // Use the same key logic as in scheduleGrid
              const periodValue = slot.period;
              const slotId = slot.id;
              const timeKey = slot.start_time ? (typeof slot.start_time === 'string' ? slot.start_time.substring(0, 5) : String(slot.start_time).substring(0, 5)) : null;
              const primaryKey = periodValue || slotId || timeKey;
              
              if (!primaryKey) return null; // Skip if no key
              
              return (
                <TableRow key={primaryKey} hover>
                  <TableCell 
                    sx={{ 
                      fontWeight: 'bold', 
                      bgcolor: 'grey.50',
                      position: 'sticky',
                      left: 0,
                      zIndex: 5,
                      bgcolor: 'grey.50'
                    }}
                  >
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight="bold">
                        {slot.start_time} - {slot.end_time}
                      </Typography>
                      {getPeriodLabel(slot) && (
                        <Typography variant="caption" color="text.secondary">
                          {getPeriodLabel(slot)}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  {classesToShow.map(cls => (
                    <React.Fragment key={cls.id}>
                      {renderScheduleCell(primaryKey, cls.id)}
                    </React.Fragment>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Statistics Section - includes temporary changes */}
      {statistics.teacherStatsArray.length > 0 || statistics.subjectStatsArray.length > 0 ? (
        <Paper elevation={2} sx={{ mt: 3, p: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            الإحصائيات {draftSchedules.length > 0 && 
              <Chip label="تشمل المسودات" color="warning" size="small" sx={{ ml: 1 }} />}
          </Typography>
          <Divider sx={{ my: 2 }} />
          
          <Grid container spacing={3}>
            {/* Teacher Statistics */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                <PersonIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                إحصائيات المعلمين
              </Typography>
              <Stack spacing={1} sx={{ mt: 2 }}>
                {statistics.teacherStatsArray.map((stat, idx) => (
                  <Card key={idx} variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight="bold">
                        {stat.name}
                      </Typography>
                      <Chip 
                        label={`${stat.totalHours.toFixed(1)} ساعة`} 
                        size="small" 
                        color="primary" 
                      />
                    </Stack>
                    <Stack useFlexGap direction="row" spacing={2} sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {stat.classCount} حصة
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stat.subjectCount} مادة
                      </Typography>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </Grid>
            
            {/* Subject Statistics */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                <BookIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                إحصائيات المواد
              </Typography>
              <Stack spacing={1} sx={{ mt: 2 }}>
                {statistics.subjectStatsArray.map((stat, idx) => (
                  <Card key={idx} variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight="bold">
                        {stat.name}
                      </Typography>
                      <Chip 
                        label={`${stat.totalHours.toFixed(1)} ساعة`} 
                        size="small" 
                        color="secondary" 
                      />
                    </Stack>
                    <Stack useFlexGap direction="row" spacing={2} sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {stat.classCount} حصة
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stat.teacherCount} معلم
                      </Typography>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      ) : null}

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, direction: 'rtl' } }}
      >
        <DialogTitle>
          {editingCell?.schedules.length > 0 
            ? (editingCell.schedules[0]?.is_draft ? 'تعديل المسودة' : 'إنشاء مسودة جديدة (تجاوز الجدول الأصلي)')
            : 'إضافة مسودة جديدة'}
          {editingCell && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {editingCell.className} - {editingCell.slot?.start_time} - {editingCell.slot?.end_time}
            </Typography>
          )}
          <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
            ℹ️ <strong>مهم:</strong> التغييرات هنا تُحفظ كمسودات فقط ولا تغير الجدول الأصلي أبداً. 
            المسودات تبقى منفصلة ويمكن مراجعتها لاحقاً.
          </Alert>
          {editingCell?.hasOriginal && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
              ⚠️ يوجد جدول أصلي في هذا الوقت. المسودة ستظهر بدلاً منه في هذا العرض فقط.
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Day selection for weekly/monthly views */}
            {(dateFilter === 'weekly' || dateFilter === 'monthly') && (
              <FormControl fullWidth>
                <InputLabel>اليوم *</InputLabel>
                <Select
                  value={selectedDayForEdit || editingCell?.dayOfWeek || ''}
                  onChange={(e) => setSelectedDayForEdit(e.target.value)}
                  label="اليوم *"
                >
                  <MenuItem value="sunday">الأحد</MenuItem>
                  <MenuItem value="monday">الاثنين</MenuItem>
                  <MenuItem value="tuesday">الثلاثاء</MenuItem>
                  <MenuItem value="wednesday">الأربعاء</MenuItem>
                  <MenuItem value="thursday">الخميس</MenuItem>
                  <MenuItem value="friday">الجمعة</MenuItem>
                  <MenuItem value="saturday">السبت</MenuItem>
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth>
              <InputLabel>المادة *</InputLabel>
              <Select
                value={editFormData.subject && subjectsList.some(s => s.id === editFormData.subject) ? editFormData.subject : ''}
                onChange={(e) => handleSubjectChange(e.target.value)}
                label="المادة *"
                disabled={loading.bulkCreatingDrafts}
                error={editFormData.subject && !subjectsList.some(s => s.id === editFormData.subject)}
              >
                {subjectsList.length === 0 ? (
                  <MenuItem disabled>جاري تحميل المواد...</MenuItem>
                ) : (
                  subjectsList.map((subject) => (
                    <MenuItem key={subject.id} value={subject.id}>
                      {subject.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {editFormData.subject && !subjectsList.some(s => s.id === editFormData.subject) && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  المادة المحددة غير متاحة. يرجى اختيار مادة أخرى.
                </Typography>
              )}
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>المعلم</InputLabel>
              <Select
                value={editFormData.teacher && staffList.filter(s => s.is_teacher && !s.is_archived).some(t => t.id === editFormData.teacher) ? editFormData.teacher : ''}
                onChange={(e) => setEditFormData({ ...editFormData, teacher: e.target.value })}
                label="المعلم"
                error={editFormData.teacher && !staffList.filter(s => s.is_teacher && !s.is_archived).some(t => t.id === editFormData.teacher)}
              >
                <MenuItem value="">لا يوجد</MenuItem>
                {staffList
                  .filter(s => s.is_teacher && !s.is_archived)
                  .map((teacher) => (
                    <MenuItem key={teacher.id} value={teacher.id}>
                      {teacher.first_name} {teacher.last_name}
                    </MenuItem>
                  ))}
              </Select>
              {editFormData.teacher && !staffList.filter(s => s.is_teacher && !s.is_archived).some(t => t.id === editFormData.teacher) && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  المعلم المحدد غير متاح. يرجى اختيار معلم آخر.
                </Typography>
              )}
            </FormControl>

            <TextField
              label="القاعة"
              value={editFormData.classroom}
              onChange={(e) => setEditFormData({ ...editFormData, classroom: e.target.value })}
              fullWidth
            />

            <TextField
              label="ملاحظات"
              value={editFormData.notes}
              onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          {editingCell?.schedules.length > 0 && editingCell.schedules[0]?.is_draft && (
            <Button
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteSchedule}
              disabled={loading.bulkCreatingDrafts}
            >
              حذف المسودة
            </Button>
          )}
          <Button
            onClick={() => setEditDialogOpen(false)}
            disabled={loading.bulkCreatingDrafts}
          >
            إلغاء
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveSchedule}
            disabled={loading.bulkCreatingDrafts || !editFormData.subject}
          >
            {editingCell?.schedules.length > 0 && editingCell.schedules[0]?.is_draft ? 'تحديث المسودة' : 'حفظ كمسودة'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EducationalScheduleClassView;
